import { FC, memo, useMemo } from 'react';
import { TaskCenterTypes, TaskCenterTypesV2 } from '@frontend/api-task-center';
import { PaginatedList, EmptyStateConfig } from '@frontend/components';
import { AutoAssignTaskSettingsBanner } from '@frontend/high-value-adoption';
import { useTranslation, i18next } from '@frontend/i18n';
import { useContactTaskListStore, useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import { useFilterPrivateTasks } from '../hooks/use-filter-private-tasks';
import { useGetFirstCallIntelAutoAssignedTaskId } from '../hooks/use-get-first-call-intel-auto-assigned-task-id';
import { taskCenterTrackingIds } from '../tracking-ids';
import { getTaskEmptyState } from '../utils';
import { prepareTaskFilters, preparePagination } from '../utils/prepare-task-filters';
import { TaskItem } from './task-item/task-item';

const EMPTY_TASK_LIST: TaskCenterTypes.Task[] = [];

const CONTACT_PANEL_EMPTY_TASKS_STATE: EmptyStateConfig = {
  type: 'contactPanelTaskList',
  header: i18next.t('No tasks related to this patient. Create a task to track important actions and follow-ups.', {
    ns: 'task-center',
  }),
  description: '',
  styles: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
};

interface Props {
  patientId?: string;
  status?: TaskCenterTypes.TaskStatus;
  listQueryEnabled: boolean;
  onCreateTask: () => void;
}

export const PaginatedTaskList: FC<Props> = memo(({ status, patientId, listQueryEnabled, onCreateTask }) => {
  const { t } = useTranslation('task-center');
  const {
    locationIds,
    assigneeFilter,
    selectedFilters: globalSelectedFilters,
  } = useTaskCenterStore(['locationIds', 'assigneeFilter', 'selectedFilters']);
  const contactPanelSelectedFilters = useContactTaskListStore((s) => s.selectedFilters);

  const selectedFilters = contactPanelSelectedFilters || globalSelectedFilters;
  const isFromContactPanel = Boolean(patientId);

  const filters = useMemo(
    () =>
      prepareTaskFilters({
        locationIds,
        selectedFilters,
        assigneeFilter,
        status: status as TaskCenterTypesV2.SchemaTypes.Enums.TaskStatus,
        patientId: isFromContactPanel ? patientId : undefined,
      }),
    [locationIds, selectedFilters, assigneeFilter, status, isFromContactPanel, patientId]
  );

  const pagination = useMemo(() => preparePagination(selectedFilters), [selectedFilters]);

  const {
    list,
    hasNextPage,
    status: queryStatus,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useFilterPrivateTasks({ filters, pagination, enabled: listQueryEnabled });

  const firstCallIntelAutoAssignedTaskId = useGetFirstCallIntelAutoAssignedTaskId(locationIds, list);

  const emptyStateConfig: EmptyStateConfig = {
    type: getTaskEmptyState(status),
    ...(status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && {
      action: {
        label: t('Create Task'),
        onClick: onCreateTask,
        trackingId: taskCenterTrackingIds.createTaskButton,
        'aria-label': t('Create Task'),
      },
    }),
    header: t('No Tasks Found'),
    description: t('Try adjusting filters, check other tabs, or create a new task to get started.'),
    ...(isFromContactPanel && CONTACT_PANEL_EMPTY_TASKS_STATE),
  };

  return (
    <PaginatedList
      listData={list ?? EMPTY_TASK_LIST}
      renderListItem={({ listItem }) => (
        <TaskItem key={listItem.id} {...listItem}>
          {listItem.id === firstCallIntelAutoAssignedTaskId && <AutoAssignTaskSettingsBanner />}
        </TaskItem>
      )}
      hasNextPage={hasNextPage}
      isError={queryStatus === 'error'}
      isLoading={isLoading || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={20}
      loadMore={fetchNextPage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={t('You have reached the end of the list 🎉')}
      height='100%'
    />
  );
});

PaginatedTaskList.displayName = 'PaginatedTaskList';
